"use client";

import React, { useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  UserCircleIcon,
  BuildingOfficeIcon,
  PuzzlePieceIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import permissionsService from "@/services/permissionsService";
import { cn } from "@/utils/utils";

interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  permission?: string;
}

const navItems: NavItem[] = [
  {
    label: "User Profile",
    path: "/hub/settings/profile",
    icon: UserCircleIcon,
  },
  {
    label: "Company Profile",
    path: "/hub/settings/company",
    icon: BuildingOfficeIcon,
    permission: "company-profile",
  },
  {
    label: "Subscription",
    path: "/hub/settings/subscription",
    icon: DocumentTextIcon,
    permission: "company-profile",
  },
  {
    label: "Integrations",
    path: "/hub/settings/integrations",
    icon: PuzzlePieceIcon,
    permission: "integrations",
  },
  {
    label: "Annual Revenue",
    path: "/hub/settings/annual-revenue",
    icon: CurrencyDollarIcon,
    permission: "annual-revenue",
  },
];

const SettingsLayout: React.FC = () => {
  const { hasPermission } = permissionsService();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle legacy deep link pattern from location.state
  useEffect(() => {
    const state = location.state as { defaultTab?: string } | null;
    if (state?.defaultTab === "servicetitan") {
      navigate("/hub/settings/integrations/servicetitan", { replace: true });
    } else if (state?.defaultTab === "quickbooks") {
      navigate("/hub/settings/integrations/quickbooks", { replace: true });
    }
  }, [location.state, navigate]);

  const filteredNavItems = navItems.filter((item) => {
    if (!item.permission) return true;
    return hasPermission(item.permission);
  });

  return (
    <MainPageWrapper error={null} loading={false} title="Settings">
      <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Side Navigation */}
          <nav className="lg:w-64 flex-shrink-0">
            <ul className="space-y-1">
              {filteredNavItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors",
                        isActive
                          ? "bg-indigo-50 text-indigo-700 border-l-4 border-indigo-600 dark:bg-indigo-950 dark:text-indigo-300 dark:border-indigo-400"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-slate-800 dark:hover:text-white",
                      )
                    }
                    to={item.path}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Content Area */}
          <div className="flex-1 min-w-0">
            <Outlet />
          </div>
        </div>
      </div>
    </MainPageWrapper>
  );
};

export default SettingsLayout;
